import "./submission-modal.scss";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import SubmissionAccordion from "../submission-accordion/submission-accordion";
import { CrossIcon } from "../../../../../assets/svg";
import { Separator } from "../../../../separator";
import {
  SolicitorCompletionDetail,
  SolicitorCompletionResponse,
  SolicitorLodgementResponse,
} from "../../../../../models/classes/solicitor/SolicitorCompletionDetail.class";
import { StageDMessageInfo, StageDocumentsInfo } from "../../../../../models/classes/stage-d/Review-Application.class";
import moment from "moment";
import { GroupSubmittedDocument } from "../../../../../models/classes/solicitor/SolicitorSubmission.class";
import { useAppSelector } from "../../../../../redux/hook";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { ModalBackground } from "../../../shared/modal-background";

export interface SubmissionModalHandle {
  getIsShowing: () => boolean;
  setIsShowing: (value: boolean) => void;
}

const SubmissionModal = forwardRef<SubmissionModalHandle, {}>(function SubmissionModal(props, ref) {
  // States
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  const { solicitorCompletionData } = useSolicitorCaseDetailsContext();

  const { solicitorSubmissionsData, solicitorSubmissionsIsFetching } = useSolicitorCaseDetailsContext();

  const [isShowing, setIsShowing] = useState<boolean>(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Derived values
  const flattenedAndSortedSubmissions = useMemo(
    () =>
      flattenAndSortData(
        solicitorSubmissionsData?.message_info,
        solicitorSubmissionsData?.folders,
        solicitorSubmissionsData?.grouped_additional_documents,
        solicitorCompletionData
      ),
    [solicitorSubmissionsData, solicitorCompletionData]
  );

  useImperativeHandle(
    ref,
    () => {
      return {
        getIsShowing: () => isShowing,
        setIsShowing: (value: boolean) => {
          setIsShowing(value);
        },
      };
    },
    [isShowing]
  );

  if (solicitorSubmissionsIsFetching || !isShowing) return null;

  return (
    <ModalBackground innerComponentRef={modalContainerRef} callback={() => setIsShowing(false)}>
      <div ref={modalContainerRef} className="polymer-submission-modal__container">
        <div className="polymer-submission-modal__title-container">
          <h2 className="polymer-submission-modal__title">View submissions</h2>
          <button onClick={() => setIsShowing(false)}>
            <CrossIcon fillColor="#0C8188" size={20} />
          </button>
        </div>
        <Separator color="#CED4DA" variant="solid" width={0.5} style={{ marginBottom: 24 }} />

        <div className="polymer-submission-modal__container-scrollable">
          {flattenedAndSortedSubmissions.map((submission, index) => (
            <SubmissionAccordion key={index} submission={submission} />
          ))}
        </div>
      </div>
    </ModalBackground>
  );
});

function getMoment(submission: SolicitorCompletionResponse | SolicitorLodgementResponse | StageDocumentsInfo | StageDMessageInfo | GroupSubmittedDocument) {
  if (submission instanceof SolicitorCompletionResponse || submission instanceof SolicitorLodgementResponse) {
    return moment(submission.submitted_at);
  }

  if (submission instanceof StageDocumentsInfo) {
    const repliedDateTimes = submission.comments.map((comment) => comment.replied_at).filter((repliedAt) => repliedAt ?? "");
    const moments = repliedDateTimes.map((repliedDateTime) => moment(repliedDateTime));
    return moment.max(moments);
  }

  if (submission instanceof GroupSubmittedDocument) {
    return submission.submitted_date_time ? moment(submission.submitted_date_time) : moment();
  }

  return moment(submission.replied_at);
}

const flattenAndSortData = (
  message_info?: StageDMessageInfo[],
  folders?: StageDocumentsInfo[],
  grouped_additional_documents?: GroupSubmittedDocument[],
  completion?: SolicitorCompletionDetail
) => {
  const flattenedSubmissions = [
    ...(message_info ?? []),
    ...(folders ?? []),
    ...(grouped_additional_documents ?? []),
    ...(completion?.completion ? [completion.completion] : []),
    ...(completion?.lodgement ? [completion.lodgement] : []),
  ];

  return flattenedSubmissions.sort((submissionA, submissionB) => {
    const momentA = getMoment(submissionA);
    const momentB = getMoment(submissionB);
    return momentB.diff(momentA);
  });
};

export { SubmissionModal };
